import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  applyStockTake,
  cancelStockTake,
  getStockTake,
  listStockTakes,
  markCounted,
  openStockTake,
  setLineCount,
  type StockTakeDTO,
  type StockTakeLineDTO,
} from './stock-take.api';
import { listWarehouses } from '../warehouses/warehouses.api';
import { readApiError } from '../../lib/api';

export function StockTakePage() {
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({ queryKey: ['stock-takes'], queryFn: listStockTakes });
  const warehouses = useQuery({ queryKey: ['warehouses'], queryFn: listWarehouses });

  const openMut = useMutation({
    mutationFn: openStockTake,
    onSuccess: (t) => {
      setActiveId(t.id);
      void qc.invalidateQueries({ queryKey: ['stock-takes'] });
    },
    onError: (e) => setError(readApiError(e).message),
  });

  if (activeId !== null) {
    return <ActiveTake id={activeId} onClose={() => setActiveId(null)} />;
  }

  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <h1 style={{ margin: 0 }}>Toma física de inventario</h1>
      </div>
      {error && <p className="error">{error}</p>}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <h3 style={{ marginTop: 0 }}>Iniciar nueva toma</h3>
        <p style={{ color: 'var(--color-muted)' }}>
          Selecciona la bodega para abrir una sesión que congele el stock actual y permita
          ingresar el conteo físico.
        </p>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {warehouses.data?.map((w) => (
            <button
              key={w.id}
              className="btn btn-primary"
              disabled={openMut.isPending}
              onClick={() => openMut.mutate(w.id)}
            >
              Abrir en {w.code}
            </button>
          ))}
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <h3 style={{ margin: 0, padding: '0.75rem 1rem', background: 'var(--color-bg)' }}>
          Tomas recientes
        </h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ background: 'var(--color-bg)', textAlign: 'left' }}>
              <th style={{ padding: '0.5rem 1rem' }}>ID</th>
              <th style={{ padding: '0.5rem 1rem' }}>Bodega</th>
              <th style={{ padding: '0.5rem 1rem' }}>Estado</th>
              <th style={{ padding: '0.5rem 1rem' }}>Líneas</th>
              <th style={{ padding: '0.5rem 1rem' }}>Creada</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.data?.map((t) => (
              <tr key={t.id} style={{ borderTop: '1px solid var(--color-border)' }}>
                <td style={{ padding: '0.5rem 1rem' }}>
                  <code>ST-{t.id}</code>
                </td>
                <td style={{ padding: '0.5rem 1rem' }}>{t.warehouseId}</td>
                <td style={{ padding: '0.5rem 1rem' }}>{statusLabel(t.status)}</td>
                <td style={{ padding: '0.5rem 1rem' }}>{t._count.lines}</td>
                <td style={{ padding: '0.5rem 1rem' }}>
                  {new Date(t.createdAt).toLocaleDateString('es-CO')}
                </td>
                <td style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>
                  <button className="btn" onClick={() => setActiveId(t.id)}>
                    Abrir
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ActiveTake({ id, onClose }: { id: number; onClose: () => void }) {
  const qc = useQueryClient();
  const [filter, setFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const take = useQuery({
    queryKey: ['stock-take', id],
    queryFn: () => getStockTake(id),
    refetchInterval: 15_000,
  });

  const countMut = useMutation({
    mutationFn: (vars: { productId: number; countedQty: number }) =>
      setLineCount(id, { productId: vars.productId, countedQty: vars.countedQty }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stock-take', id] }),
    onError: (e) => setError(readApiError(e).message),
  });

  const markCountedMut = useMutation({
    mutationFn: () => markCounted(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stock-take', id] }),
    onError: (e) => setError(readApiError(e).message),
  });

  const applyMut = useMutation({
    mutationFn: () => applyStockTake(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stock-take', id] });
      void qc.invalidateQueries({ queryKey: ['stock-takes'] });
      void qc.invalidateQueries({ queryKey: ['products'] });
      onClose();
    },
    onError: (e) => setError(readApiError(e).message),
  });

  const cancelMut = useMutation({
    mutationFn: () => cancelStockTake(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stock-takes'] });
      onClose();
    },
    onError: (e) => setError(readApiError(e).message),
  });

  if (take.isLoading) return <p>Cargando...</p>;
  if (!take.data) return <p className="error">Toma no encontrada</p>;
  const t: StockTakeDTO = take.data;

  const filtered = t.lines.filter(
    (l) =>
      filter.trim() === '' ||
      l.product.sku.toLowerCase().includes(filter.toLowerCase()) ||
      l.product.name.toLowerCase().includes(filter.toLowerCase()),
  );

  const uncounted = t.lines.filter((l) => l.countedQty === null).length;
  const withDiff = t.lines.filter((l) => {
    if (l.countedQty === null) return false;
    return Number(l.countedQty) !== Number(l.expectedQty);
  }).length;

  return (
    <section>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <button className="btn" onClick={onClose}>
          ← Tomas
        </button>
        <h1 style={{ margin: 0 }}>Toma ST-{t.id}</h1>
        <span
          style={{
            background: 'var(--color-bg)',
            padding: '4px 10px',
            borderRadius: 999,
            fontSize: '0.8rem',
          }}
        >
          {statusLabel(t.status)}
        </span>
      </div>

      {error && <p className="error">{error}</p>}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '0.75rem',
          marginBottom: '1rem',
        }}
      >
        <Kpi label="Líneas totales" value={String(t.lines.length)} />
        <Kpi label="Sin contar" value={String(uncounted)} accent={uncounted > 0} />
        <Kpi label="Con diferencia" value={String(withDiff)} />
      </div>

      <div className="card" style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
        <input
          className="input"
          placeholder="Filtrar por SKU o nombre..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{ flex: 1 }}
        />
        {t.status === 'DRAFT' && (
          <button
            className="btn btn-primary"
            disabled={markCountedMut.isPending || uncounted > 0}
            onClick={() => markCountedMut.mutate()}
          >
            Marcar como contado
          </button>
        )}
        {t.status === 'COUNTED' && (
          <button
            className="btn btn-primary"
            disabled={applyMut.isPending}
            onClick={() => {
              if (
                confirm(
                  `Aplicar ajuste a ${withDiff} línea(s) con diferencia? Esto modifica el stock real.`,
                )
              ) {
                applyMut.mutate();
              }
            }}
          >
            Aplicar ajustes ({withDiff})
          </button>
        )}
        {(t.status === 'DRAFT' || t.status === 'COUNTED') && (
          <button
            className="btn"
            onClick={() => {
              if (confirm('Cancelar la toma? Las cuentas se descartan.')) {
                cancelMut.mutate();
              }
            }}
          >
            Cancelar
          </button>
        )}
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ background: 'var(--color-bg)', textAlign: 'left' }}>
              <th style={{ padding: '0.5rem 1rem' }}>SKU</th>
              <th style={{ padding: '0.5rem 1rem' }}>Producto</th>
              <th style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>Sistema</th>
              <th style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>Conteo</th>
              <th style={{ padding: '0.5rem 1rem', textAlign: 'right' }}>Δ</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((line) => (
              <LineRow
                key={line.id}
                line={line}
                editable={t.status === 'DRAFT' || t.status === 'COUNTED'}
                onCount={(qty) => countMut.mutate({ productId: line.productId, countedQty: qty })}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function LineRow({
  line,
  editable,
  onCount,
}: {
  line: StockTakeLineDTO;
  editable: boolean;
  onCount: (qty: number) => void;
}) {
  const [draft, setDraft] = useState<string>(
    line.countedQty !== null ? String(Number(line.countedQty)) : '',
  );
  const expected = Number(line.expectedQty);
  const counted = line.countedQty !== null ? Number(line.countedQty) : null;
  const delta = counted !== null ? counted - expected : null;

  return (
    <tr style={{ borderTop: '1px solid var(--color-border)' }}>
      <td style={{ padding: '0.4rem 1rem' }}>
        <code>{line.product.sku}</code>
      </td>
      <td style={{ padding: '0.4rem 1rem' }}>{line.product.name}</td>
      <td style={{ padding: '0.4rem 1rem', textAlign: 'right' }}>{expected}</td>
      <td style={{ padding: '0.4rem 1rem', textAlign: 'right' }}>
        {editable ? (
          <input
            className="input"
            type="number"
            min={0}
            step="0.001"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => {
              if (draft.trim() === '') return;
              const n = Number(draft);
              if (!Number.isFinite(n) || n < 0) return;
              if (n === counted) return;
              onCount(n);
            }}
            style={{ width: 80, padding: '2px 6px', textAlign: 'right' }}
          />
        ) : counted !== null ? (
          counted
        ) : (
          <em style={{ color: 'var(--color-muted)' }}>—</em>
        )}
      </td>
      <td
        style={{
          padding: '0.4rem 1rem',
          textAlign: 'right',
          color: delta === null || delta === 0 ? 'var(--color-muted)' : delta > 0 ? 'var(--color-accent)' : 'var(--color-danger)',
          fontWeight: delta !== null && delta !== 0 ? 600 : 400,
        }}
      >
        {delta === null ? '—' : delta > 0 ? `+${delta}` : delta}
      </td>
    </tr>
  );
}

function Kpi({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="card" style={{ borderLeft: accent ? '4px solid var(--color-accent)' : undefined }}>
      <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--color-muted)' }}>
        {label}
      </div>
      <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function statusLabel(s: string): string {
  switch (s) {
    case 'DRAFT':
      return 'Borrador';
    case 'COUNTED':
      return 'Contado';
    case 'APPLIED':
      return 'Aplicado';
    case 'CANCELLED':
      return 'Cancelado';
    default:
      return s;
  }
}
